/**
 * GoGrabJob Logger - log4j-style logging for browser
 * Reads configuration from ggjlog.properties
 */

export enum LogLevel {
  ALL = 0,
  TRACE = 1,
  DEBUG = 2,
  INFO = 3,
  WARN = 4,
  ERROR = 5,
  OFF = 6
}

interface LogEntry {
  timestamp: string;
  level: string;
  package: string;
  message: string;
  data?: any;
  caller?: string;
  stack?: string;
}

interface LoggerConfig {
  level: LogLevel;
  consoleEnabled: boolean;
  storageEnabled: boolean;
  storageMaxEntries: number;
  packageLevels: Record<string, LogLevel>;
  timestamp: boolean;
  stackTrace: boolean;
  caller: boolean;
  auditEnabled: boolean;
  performanceEnabled: boolean;
  performanceThreshold: number;
}

// Parse config from properties file
const parseConfig = (): LoggerConfig => {
  return {
    level: LogLevel.INFO,
    consoleEnabled: true,
    storageEnabled: true,
    storageMaxEntries: 500,
    packageLevels: {
      'Auth': LogLevel.DEBUG,
      'OAuth': LogLevel.DEBUG,
      'Database': LogLevel.INFO,
      'API': LogLevel.WARN,
      'UI': LogLevel.WARN
    },
    timestamp: true,
    stackTrace: true,
    caller: true,
    auditEnabled: true,
    performanceEnabled: true,
    performanceThreshold: 100
  };
};

const config = parseConfig();
const STORAGE_KEY = 'ggj_logs';

class Logger {
  private packageName: string;

  constructor(packageName: string) {
    this.packageName = packageName;
  }

  private getEffectiveLevel(): LogLevel {
    return config.packageLevels[this.packageName] ?? config.level;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.getEffectiveLevel();
  }

  private getCaller(): string {
    if (!config.caller) return '';
    
    try {
      const stack = new Error().stack;
      const lines = stack?.split('\n') || [];
      // Skip first 3 lines (Error, this function, log function)
      const callerLine = lines[3];
      const match = callerLine?.match(/at\s+(.+?)\s+\(/);
      return match ? match[1] : '';
    } catch {
      return '';
    }
  }

  private formatMessage(level: string, message: string, data?: any): string {
    const parts: string[] = [];
    
    if (config.timestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }
    
    parts.push(`[${level}]`);
    parts.push(`[${this.packageName}]`);
    
    const caller = this.getCaller();
    if (caller) {
      parts.push(`[${caller}]`);
    }
    
    parts.push(message);
    
    return parts.join(' ');
  }

  private writeToStorage(entry: LogEntry): void {
    if (!config.storageEnabled) return;

    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      const logs: LogEntry[] = existing ? JSON.parse(existing) : [];
      
      logs.push(entry);
      
      // Keep only max entries
      if (logs.length > config.storageMaxEntries) {
        logs.splice(0, logs.length - config.storageMaxEntries);
      }
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(logs));
    } catch (err) {
      // Storage might be full, log error for debugging
      console.error('❌ Logger: Failed to write to localStorage:', err);
    }
  }

  private log(level: LogLevel, levelName: string, message: string, data?: any): void {
    if (!this.shouldLog(level)) return;

    const formatted = this.formatMessage(levelName, message, data);
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: levelName,
      package: this.packageName,
      message,
      data,
      caller: config.caller ? this.getCaller() : undefined
    };

    // Console output
    if (config.consoleEnabled) {
      const emoji = level === LogLevel.ERROR ? '❌' : 
                   level === LogLevel.WARN ? '⚠️' : 
                   level === LogLevel.INFO ? 'ℹ️' : 
                   level === LogLevel.DEBUG ? '🔍' : 
                   level === LogLevel.TRACE ? '📝' : 
                   '✅';
      
      const consoleMethod = level === LogLevel.ERROR ? console.error :
                           level === LogLevel.WARN ? console.warn :
                           console.log;
      
      if (data !== undefined) {
        consoleMethod(`${emoji} ${formatted}`, data);
      } else {
        consoleMethod(`${emoji} ${formatted}`);
      }
    }

    // Storage output
    this.writeToStorage(entry);
  }

  trace(message: string, data?: any): void {
    this.log(LogLevel.TRACE, 'TRACE', message, data);
  }

  debug(message: string, data?: any): void {
    this.log(LogLevel.DEBUG, 'DEBUG', message, data);
  }

  info(message: string, data?: any): void {
    this.log(LogLevel.INFO, 'INFO', message, data);
  }

  warn(message: string, data?: any): void {
    this.log(LogLevel.WARN, 'WARN', message, data);
  }

  error(message: string, data?: any): void {
    this.log(LogLevel.ERROR, 'ERROR', message, data);
    
    if (config.stackTrace && data instanceof Error) {
      this.log(LogLevel.ERROR, 'STACK', data.stack || '', undefined);
    }
  }

  // Audit logging (always enabled for critical operations)
  audit(operation: string, details: any): void {
    if (!config.auditEnabled) return;
    
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'AUDIT',
      package: this.packageName,
      message: `AUDIT: ${operation}`,
      data: details
    };

    console.log(`🔒 [AUDIT] [${this.packageName}] ${operation}`, details);
    this.writeToStorage(entry);
  }

  // Performance logging
  performance(operation: string, durationMs: number, details?: any): void {
    if (!config.performanceEnabled) return;
    if (durationMs < config.performanceThreshold) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'PERF',
      package: this.packageName,
      message: `${operation} took ${durationMs}ms`,
      data: { durationMs, ...details }
    };

    console.log(`⚡ [PERF] [${this.packageName}] ${operation}: ${durationMs}ms`, details);
    this.writeToStorage(entry);
  }

  // Timer utility
  startTimer(operation: string): () => void {
    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      this.performance(operation, duration);
    };
  }
}

// Factory function
export const getLogger = (packageName: string): Logger => {
  const loggerInstance = new Logger(packageName);
  console.log(`📋 Logger initialized for package: ${packageName}`);
  return loggerInstance;
};

// Utility functions for log management
export const getLogs = (): LogEntry[] => {
  try {
    const logs = localStorage.getItem(STORAGE_KEY);
    return logs ? JSON.parse(logs) : [];
  } catch {
    return [];
  }
};

export const clearLogs = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export const downloadLogs = (): void => {
  const logs = getLogs();
  const blob = new Blob([JSON.stringify(logs, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ggj_logs_${new Date().toISOString()}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

export const filterLogs = (filters: {
  level?: string;
  package?: string;
  startTime?: Date;
  endTime?: Date;
  search?: string;
}): LogEntry[] => {
  let logs = getLogs();

  if (filters.level) {
    logs = logs.filter(log => log.level === filters.level);
  }

  if (filters.package) {
    logs = logs.filter(log => log.package === filters.package);
  }

  if (filters.startTime) {
    logs = logs.filter(log => new Date(log.timestamp) >= filters.startTime!);
  }

  if (filters.endTime) {
    logs = logs.filter(log => new Date(log.timestamp) <= filters.endTime!);
  }

  if (filters.search) {
    const search = filters.search.toLowerCase();
    logs = logs.filter(log => 
      log.message.toLowerCase().includes(search) ||
      JSON.stringify(log.data).toLowerCase().includes(search)
    );
  }

  return logs;
};

